import { useCallback, useEffect, useRef, useState } from 'react';
import { MAX_VOICE_DURATION_MS, WAVEFORM_PEAKS } from './chatConstants';

// Safari не умеет webm/opus, поэтому пишем принудительно в audio/mp4 (m4a/aac).
const PREFERRED_MIME_TYPES = ['audio/mp4', 'audio/mp4;codecs=mp4a.40.2', 'audio/aac'];

const pickMimeType = () => {
  if (typeof MediaRecorder === 'undefined') return null;
  const supported = PREFERRED_MIME_TYPES.find((type) => MediaRecorder.isTypeSupported(type));
  // Если mp4 не поддерживается (старый Firefox) — пишем дефолтом, но это крайний случай.
  return supported || '';
};

// Бэкенд сверяет contentType с набором вида "audio/mp4", а браузер отдаёт
// mimeType с параметрами ("audio/mp4;codecs=mp4a.40.2") — параметры срезаем,
// чтобы запись гарантированно опознавалась как голосовая.
const baseMimeType = (mimeType) => String(mimeType || 'audio/mp4').split(';')[0].trim();

// Сжимаем произвольное число замеров до WAVEFORM_PEAKS столбиков (max-pooling).
const normalizePeaks = (raw, target = WAVEFORM_PEAKS) => {
  if (raw.length === 0) return [];
  if (raw.length <= target) return raw;
  const step = raw.length / target;
  return Array.from({ length: target }, (_, i) =>
    Math.max(...raw.slice(Math.floor(i * step), Math.floor((i + 1) * step))));
};

const buildWaveform = (samples) => normalizePeaks(samples).join(',');

export const useVoiceRecorder = () => {
  const [recording, setRecording] = useState(false);
  const [elapsedMs, setElapsedMs] = useState(0);
  const [error, setError] = useState(null);

  const recorderRef = useRef(null);
  const streamRef = useRef(null);
  const audioCtxRef = useRef(null);
  const analyserRef = useRef(null);
  const samplesRef = useRef([]);
  const chunksRef = useRef([]);
  const startedAtRef = useRef(0);
  const peakTimerRef = useRef(null);
  const tickRef = useRef(null);
  const autoStopRef = useRef(null);
  const resolveRef = useRef(null);
  const cancelledRef = useRef(false);

  const cleanup = useCallback(() => {
    clearInterval(peakTimerRef.current);
    clearInterval(tickRef.current);
    clearTimeout(autoStopRef.current);
    streamRef.current?.getTracks().forEach((track) => track.stop());
    audioCtxRef.current?.close().catch(() => {});
    streamRef.current = null;
    audioCtxRef.current = null;
    analyserRef.current = null;
    recorderRef.current = null;
  }, []);

  useEffect(() => cleanup, [cleanup]);

  const start = useCallback(async () => {
    setError(null);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;

      const AudioCtx = window.AudioContext || window.webkitAudioContext;
      const audioCtx = new AudioCtx();
      const source = audioCtx.createMediaStreamSource(stream);
      const analyser = audioCtx.createAnalyser();
      analyser.fftSize = 256;
      source.connect(analyser);
      audioCtxRef.current = audioCtx;
      analyserRef.current = analyser;

      const buffer = new Uint8Array(analyser.frequencyBinCount);
      samplesRef.current = [];
      chunksRef.current = [];
      cancelledRef.current = false;

      // Амплитудный пик снимаем раз в 100 мс, в конце схлопываем до WAVEFORM_PEAKS.
      peakTimerRef.current = setInterval(() => {
        if (recorderRef.current?.state !== 'recording') return;
        analyser.getByteTimeDomainData(buffer);
        let peak = 0;
        for (let i = 0; i < buffer.length; i += 1) {
          const value = Math.abs(buffer[i] - 128);
          if (value > peak) peak = value;
        }
        samplesRef.current.push(Math.round((peak / 128) * 100));
      }, 100);

      const mimeType = pickMimeType();
      const recorder = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);
      recorderRef.current = recorder;

      recorder.ondataavailable = (event) => {
        if (event.data && event.data.size > 0) chunksRef.current.push(event.data);
      };

      recorder.onstop = () => {
        const durationMs = Date.now() - startedAtRef.current;
        const type = baseMimeType(recorder.mimeType || mimeType);
        const blob = new Blob(chunksRef.current, { type });
        const waveform = buildWaveform(samplesRef.current);
        cleanup();
        setRecording(false);
        setElapsedMs(0);

        const resolve = resolveRef.current;
        resolveRef.current = null;
        if (!resolve) return;
        if (cancelledRef.current || blob.size === 0) {
          resolve(null);
          return;
        }
        const extension = type.includes('mp4') || type.includes('aac') ? 'm4a' : 'webm';
        resolve({
          blob,
          file: new File([blob], `voice-${Date.now()}.${extension}`, { type }),
          durationMs,
          waveform,
        });
      };

      startedAtRef.current = Date.now();
      recorder.start(250);
      setRecording(true);
      setElapsedMs(0);

      tickRef.current = setInterval(() => {
        setElapsedMs(Date.now() - startedAtRef.current);
      }, 200);

      // Максимум 3 минуты — дальше останавливаемся сами.
      autoStopRef.current = setTimeout(() => {
        if (recorderRef.current?.state === 'recording') recorderRef.current.stop();
      }, MAX_VOICE_DURATION_MS);

      return true;
    } catch (e) {
      cleanup();
      setRecording(false);
      setError(e?.message || 'Не удалось получить доступ к микрофону');
      return false;
    }
  }, [cleanup]);

  // Возвращает { file, durationMs, waveform } либо null, если запись отменена.
  const stop = useCallback(() => new Promise((resolve) => {
    if (!recorderRef.current || recorderRef.current.state === 'inactive') {
      resolve(null);
      return;
    }
    resolveRef.current = resolve;
    recorderRef.current.stop();
  }), []);

  const cancel = useCallback(() => {
    cancelledRef.current = true;
    return stop();
  }, [stop]);

  return { recording, elapsedMs, error, start, stop, cancel };
};

export default useVoiceRecorder;
